/* eslint-disable react/jsx-props-no-spreading */
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import injectSheet from 'react-jss';
import { useCombobox } from 'downshift';

import { areEqual } from 'Utils/areEqual';
import CheckBox from 'Components/CheckBox';
import Tooltip from 'HOC/Tooltip';
import styleSheet from './styles';

interface Props {
  classes: any;
  items: any[];
  selectedOptions: any[];
  placeholder: string;
  selectionDescription?: string;
  onDropdownChange?: (e: object) => void;
}

const MultiSelect = (props: Props) => {
  const {
    classes,
    items,
    selectedOptions,
    placeholder,
    selectionDescription,
    onDropdownChange,
  } = props;

  const [filteredItems, setFilteredItems] = useState(items);
  // reset items if items prop changes
  useEffect(() => {
    setFilteredItems(items);
  }, [items]);

  const menuRef = useRef(null);

  const selectedOptionsObject = useMemo(() => {
    const obj = {};
    selectedOptions.forEach((selectedOption) => {
      obj[selectedOption.value] = true;
    });
    return obj;
  }, [selectedOptions]);

  const filteredAndGroupedItems = useMemo(() => {
    const selected = [];
    const notSelected = [];
    filteredItems.forEach((item) => {
      if (selectedOptionsObject[item.value]) {
        selected.push(item);
      } else {
        notSelected.push(item);
      }
    });
    return [...selected, ...notSelected];
  }, [filteredItems, selectedOptionsObject]);

  const {
    isOpen,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    getItemProps,
    highlightedIndex,
    openMenu,
  } = useCombobox({
    items: filteredAndGroupedItems,
    itemToString: (item) => (item ? item.label : ''),
    onSelectedItemChange: ({ selectedItem }) => {
      onDropdownChange({ target: { value: selectedItem } });
    },
    selectedItem: null,
    stateReducer: (state, actionAndChanges) => {
      const { changes, type } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen: true, // keep menu open after selection
            highlightedIndex: state.highlightedIndex,
            inputValue: state.inputValue, // keep search input value
          };
        case useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem:
          return {
            ...changes,
            inputValue: state.inputValue, // keep search input value
          };
        case useCombobox.stateChangeTypes.InputBlur:
          return {
            ...changes,
            selectedItem: null, // don't add/remove current highlighted item on Blur
            inputValue: '', // don't add the item string as input value at selection
          };
        default:
          return changes;
      }
    },
    onInputValueChange: ({ inputValue }) => {
      setFilteredItems(
        items.filter((item) =>
          item.label.toLowerCase().includes(inputValue.toLowerCase())
        )
      );
    },
  });

  const placeholderText = selectedOptions.length
    ? `${selectedOptions.length} ${selectionDescription || 'selected'}`
    : placeholder;

  // MultiSelect component renders twice on inputValue changes.
  // Using memo here saves having to generate item elements each time,
  // and improve responsiveness for MultiSelect with large options.
  const dropdownList = useMemo(() => {
    let shouldPlaceDivider = false;

    return filteredAndGroupedItems.map((item, index) => {
      const isSelected = selectedOptionsObject[item.value] ?? false;

      // place divider between selected and not selected items
      let divider = null;
      if (index === 0 && isSelected) {
        shouldPlaceDivider = true;
      } else if (shouldPlaceDivider && !isSelected) {
        divider = <hr className={classes.divider} />;
        shouldPlaceDivider = false;
      }

      return (
        // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={`${item.value}`}>
          {divider}
          <li
            {...getItemProps({
              item,
              index,
              'aria-selected': isSelected,
            })}
            style={
              highlightedIndex === index ? { backgroundColor: '#bde4ff' } : {}
            }
            className={classes.dropdownItem}
          >
            <div className={classes.dropdownItemCheckbox}>
              <CheckBox isChecked={isSelected} disallowTabbing />
            </div>
            {item.label.length > 20 ? (
              <Tooltip
                position="right"
                text={item.label}
                offsetTop={-5}
                offsetLeft={140}
                delay={0.5}
              >
                <span
                  className={`
                    ${classes.dropdownItemText} 
                    ${
                      isSelected ? classes.selectedText : classes.unSelectedText
                    }`}
                  aria-label={item.label}
                >
                  {`${item.label.slice(0, 20)}...`}
                </span>
              </Tooltip>
            ) : (
              <span
                className={`
                ${classes.dropdownItemText} 
                ${isSelected ? classes.selectedText : classes.unSelectedText}`}
              >
                {item.label}
              </span>
            )}
          </li>
        </React.Fragment>
      );
    });
  }, [filteredAndGroupedItems, selectedOptionsObject, highlightedIndex]);

  return (
    <div>
      <div {...getComboboxProps()} className={classes.multiSelectInput}>
        <input
          {...getInputProps({
            onFocus: () => {
              if (!isOpen) {
                openMenu();
                if (menuRef.current) {
                  menuRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                  });
                }
              }
            },
          })}
          className={`${classes.input} ${
            selectedOptions.length > 0 ? classes.selectedInput : ''
          }`}
          type="text"
          autoComplete="none"
          placeholder={placeholderText}
        />
      </div>
      <ul
        {...getMenuProps({ ref: menuRef })}
        className={`${isOpen ? classes.dropdown : ''} 
          ${
            selectedOptions.length > 0 ? classes.selected : classes.unSelected
          }`}
      >
        {isOpen && dropdownList}
      </ul>
    </div>
  );
};

MultiSelect.defaultProps = {
  selectionDescription: '',
  onDropdownChange: () => {},
};

export default injectSheet(styleSheet)(memo(MultiSelect, areEqual));
